/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import { motion } from 'framer-motion';
import {
  FaUserFriends, FaLightbulb, FaRocket, FaHandshake, FaRegCheckCircle, FaChartLine,
  FaLock, FaCalendarAlt, FaTools, FaMapMarkedAlt
} from 'react-icons/fa';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Styles de base
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  success: '#06d6a0',
  warning: '#ffbe0b',
  dark: '#1e2a3a',
  light: '#f8f9fa',
  darkGradient: 'linear-gradient(135deg, #0b1120 0%, #1e293b 100%)',
  glassEffect: 'rgba(255, 255, 255, 0.03)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
};

const fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const sectionStyle = {
  padding: '120px 24px',
  backgroundColor: colors.light,
  fontFamily,
  position: 'relative',
  overflow: 'hidden',
};

const sectionHeadingStyle = {
  fontSize: 'clamp(2rem, 3vw, 2.75rem)',
  fontWeight: '800',
  color: colors.dark,
  marginBottom: '16px',
  textAlign: 'center',
  position: 'relative',
};

const sectionSubheadingStyle = {
  fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
  color: '#64748b',
  textAlign: 'center',
  maxWidth: '700px',
  margin: '0 auto 60px',
  lineHeight: '1.6',
};

// Formes d'arrière-plan
const shapesStyles = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  top: 0,
  left: 0,
  zIndex: 0,
};

const circleShape = {
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(58, 134, 255, 0.15)',
  filter: 'blur(40px)',
  zIndex: 0,
  opacity: 0.5,
};

function AboutUs() {
  // Tableau pour la section "Notre histoire"
  const timeline = [
    {
      year: '2020',
      title: 'Naissance du projet',
      description: 'Fondation de Drivesoft par une équipe d\'experts passionnés par l\'innovation dans le transport sanitaire.'
    },
    {
      year: '2021',
      title: 'Premier prototype',
      description: 'Développement de la première version de notre solution après de nombreuses consultations avec des professionnels du secteur.'
    },
    {
      year: '2022',
      title: 'Expansion du marché',
      description: 'Drivesoft commence à se déployer dans plusieurs entreprises de transport sanitaire en France avec un retour client exceptionnel.'
    },
    {
      year: '2023',
      title: 'Innovations majeures',
      description: 'Intégration de l\'intelligence artificielle et de nouvelles fonctionnalités demandées par nos utilisateurs.'
    },
    {
      year: '2024',
      title: 'Vision internationale',
      description: 'Drivesoft étend ses services et prépare son expansion vers les marchés internationaux tout en renforçant son équipe.'
    }
  ];

  // Tableau pour les valeurs de l'entreprise
  const values = [
    {
      icon: <FaUserFriends />,
      title: 'Collaboration',
      description: 'Nous croyons en la puissance du travail d\'équipe, tant en interne qu\'avec nos clients.'
    },
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'L\'innovation est au cœur de notre approche pour résoudre les défis du transport sanitaire.'
    },
    {
      icon: <FaRocket />,
      title: 'Excellence',
      description: 'Nous visons constamment l\'excellence dans tous les aspects de notre travail et de nos produits.'
    },
    {
      icon: <FaHandshake />,
      title: 'Intégrité',
      description: 'L\'honnêteté et la transparence guident toutes nos interactions avec nos clients et partenaires.'
    }
  ];

  // Tableau des avantages de notre solution
  const benefits = [
    {
      icon: <FaRegCheckCircle />,
      title: 'Simplicité d\'utilisation',
      description: 'Interface intuitive adaptée aux utilisateurs de tous niveaux de compétence technique.'
    },
    {
      icon: <FaChartLine />,
      title: 'Optimisation des coûts',
      description: 'Réduction des coûts opérationnels grâce à une gestion optimisée des ressources.'
    },
    {
      icon: <FaLock />,
      title: 'Sécurité des données',
      description: 'Protection avancée des données sensibles avec des normes de sécurité de pointe.'
    },
    {
      icon: <FaCalendarAlt />,
      title: 'Planification efficace',
      description: 'Outils de planification qui optimisent les trajets et réduisent les temps d\'attente.'
    },
    {
      icon: <FaTools />,
      title: 'Support personnalisé',
      description: 'Assistance dédiée pour répondre à vos besoins spécifiques et garantir votre satisfaction.'
    },
    {
      icon: <FaMapMarkedAlt />,
      title: 'Couverture nationale',
      description: 'Notre solution s\'adapte à toutes les régions et aux spécificités locales.'
    }
  ];

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* SECTION HERO */}
      <motion.section
        style={{
          ...sectionStyle,
          background: colors.darkGradient,
          color: colors.light,
          paddingTop: '150px',
          paddingBottom: '100px',
          textAlign: 'center',
          position: 'relative'
        }}
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        {/* Éléments décoratifs */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(58, 134, 255, 0.3) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
          opacity: 0.5
        }}
        />

        <div style={{
          position: 'absolute',
          bottom: '0',
          right: '10%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 0, 110, 0.3) 0%, transparent 70%)',
          filter: 'blur(60px)',
          zIndex: 0,
          opacity: 0.5
        }}
        />

        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto'
        }}
        >
          <motion.h1
            variants={itemVariant}
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              marginBottom: '30px',
              background: 'linear-gradient(90deg, #ffffff 0%, #a2d6fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
              lineHeight: '1.2',
            }}
          >
            Qui sommes-nous
          </motion.h1>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
              fontWeight: '400',
              maxWidth: '700px',
              margin: '0 auto 50px',
              color: 'rgba(255, 255, 255, 0.85)',
              lineHeight: '1.6',
            }}
          >
            Drivesoft révolutionne la gestion du transport sanitaire avec des solutions
            innovantes et personnalisées pour optimiser vos opérations quotidiennes.
          </motion.p>
        </div>
      </motion.section>

      {/* SECTION NOTRE MISSION */}
      <motion.section
        style={{
          ...sectionStyle,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%)'
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto'
        }}
        >
          <motion.div variants={staggerVariant}>
            <motion.h2
              variants={itemVariant}
              style={{
                ...sectionHeadingStyle,
                textAlign: 'left',
                marginBottom: '25px',
                display: 'inline-block',
                position: 'relative'
              }}
            >
              Notre mission
              <div style={{
                position: 'absolute',
                bottom: '-10px',
                left: '0',
                width: '80px',
                height: '4px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                borderRadius: '2px'
              }}
              />
            </motion.h2>

            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '60px'
            }}
            >
              <motion.div variants={itemVariant} style={{ flex: '1 1 500px' }}>
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '30px',
                  color: colors.dark,
                  lineHeight: '1.3'
                }}
                >
                  Transformer le transport sanitaire par la technologie
                </h3>
                <p style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.8',
                  color: '#64748b',
                  marginBottom: '30px'
                }}
                >
                  Chez Drivesoft, notre mission est de transformer le transport sanitaire par
                  l'innovation technologique. Notre logiciel de gestion offre une solution complète
                  qui simplifie les opérations quotidiennes tout en garantissant la qualité des soins
                  aux patients.
                </p>
                <p style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.8',
                  color: '#64748b',
                  marginBottom: '30px'
                }}
                >
                  Nous sommes déterminés à créer des outils qui permettent aux professionnels du
                  transport sanitaire de se concentrer sur l'essentiel : le bien-être et la sécurité
                  des patients, tout en optimisant leur productivité et leur rentabilité.
                </p>
              </motion.div>

              <motion.div
                variants={itemVariant}
                style={{
                  flex: '1 1 400px',
                  position: 'relative',
                  height: '400px'
                }}
              >
                <div style={{
                  position: 'absolute',
                  width: '80%',
                  height: '80%',
                  background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.1) 0%, rgba(131, 56, 236, 0.1) 100%)',
                  borderRadius: '24px',
                  top: '10%',
                  left: '10%',
                  zIndex: 1,
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
                >
                  <div style={{
                    border: '2px dashed rgba(58, 134, 255, 0.3)',
                    borderRadius: '20px',
                    padding: '30px',
                    width: '80%',
                    height: '80%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: colors.dark
                  }}
                  >
                    <div style={{
                      fontSize: '50px',
                      color: colors.primary,
                      marginBottom: '20px'
                    }}
                    >
                      <FaRocket />
                    </div>
                    <h3 style={{ fontWeight: '700', marginBottom: '15px' }}>Innovation et Excellence</h3>
                    <p style={{
                      fontSize: '0.95rem', lineHeight: '1.8', color: '#475569', textAlign: 'center'
                    }}
                    >
                      Notre équipe est dédiée à l'innovation continue pour vous offrir la meilleure expérience possible.
                    </p>
                  </div>
                </div>
                <div style={{
                  position: 'absolute',
                  width: '70%',
                  height: '70%',
                  borderRadius: '24px',
                  bottom: 0,
                  right: 0,
                  zIndex: 0,
                  background: 'linear-gradient(135deg, rgba(255, 0, 110, 0.1) 0%, rgba(255, 190, 11, 0.1) 100%)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.06)'
                }}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* SECTION NOTRE HISTOIRE */}
      <motion.section
        style={{
          ...sectionStyle,
          backgroundColor: colors.light,
          position: 'relative'
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <div style={shapesStyles}>
          <div style={{
            ...circleShape,
            width: '400px',
            height: '400px',
            top: '-100px',
            right: '-100px',
            background: 'rgba(58, 134, 255, 0.08)',
          }}
          />
          <div style={{
            ...circleShape,
            width: '300px',
            height: '300px',
            bottom: '-50px',
            left: '-50px',
            background: 'rgba(131, 56, 236, 0.08)',
          }}
          />
        </div>

        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              ...sectionHeadingStyle,
              position: 'relative',
              display: 'inline-block',
              marginBottom: '60px'
            }}
          >
            Notre histoire
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '50%',
              width: '60px',
              height: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              borderRadius: '2px',
              transform: 'translateX(-50%)'
            }}
            />
          </motion.h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            maxWidth: '800px',
            margin: '0 auto'
          }}
          >
            {/* Ligne verticale de timeline */}
            <div style={{
              position: 'absolute',
              top: '0',
              bottom: '0',
              left: '50%',
              width: '2px',
              background: 'linear-gradient(to bottom, rgba(58, 134, 255, 0.3), rgba(131, 56, 236, 0.3))',
              transform: 'translateX(-50%)',
              zIndex: 0
            }}
            />

            {timeline.map((event, index) => (
              <motion.div
                key={index}
                variants={itemVariant}
                style={{
                  display: 'flex',
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  marginBottom: '60px',
                  position: 'relative',
                  width: '100%'
                }}
              >
                <div style={{
                  width: '45%',
                  position: 'relative',
                  background: 'white',
                  padding: '25px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  zIndex: 2
                }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    [index % 2 === 0 ? 'right' : 'left']: '-40px',
                    width: '40px',
                    height: '2px',
                    background: `linear-gradient(${index % 2 === 0 ? 'to right' : 'to left'}, ${colors.primary}, ${colors.accent})`,
                    zIndex: 1
                  }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    [index % 2 === 0 ? 'right' : 'left']: index % 2 === 0 ? '-70px' : '-65px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: 'white',
                    border: `2px solid ${colors.primary}`,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: colors.primary
                  }}
                  >
                    {event.year.slice(2)}
                  </div>
                  <span style={{
                    display: 'block',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: colors.dark
                  }}
                  >
                    {event.title}
                  </span>
                  <span style={{
                    display: 'block',
                    fontSize: '0.95rem',
                    color: '#64748b',
                    marginBottom: '10px'
                  }}
                  >
                    {event.year}
                  </span>
                  <p style={{
                    margin: '0',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    color: '#475569'
                  }}
                  >
                    {event.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* SECTION NOS VALEURS */}
      <motion.section
        style={{
          ...sectionStyle,
          backgroundColor: colors.darkGradient,
          color: colors.light
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              ...sectionHeadingStyle,
              color: colors.light,
              position: 'relative',
              display: 'inline-block',
              marginBottom: '60px'
            }}
          >
            Nos valeurs
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '50%',
              width: '60px',
              height: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              borderRadius: '2px',
              transform: 'translateX(-50%)'
            }}
            />
          </motion.h2>

          <motion.div
            variants={staggerVariant}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '30px'
            }}
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariant}
                style={{
                  flex: '1 1 250px',
                  maxWidth: '300px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  padding: '40px 30px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                  transition: 'transform 0.3s ease',
                }}
                whileHover={{ transform: 'translateY(-10px)' }}
              >
                <div style={{
                  fontSize: '40px',
                  color: [colors.primary, colors.secondary, colors.accent, colors.success][index % 4],
                  marginBottom: '20px'
                }}
                >
                  {value.icon}
                </div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  marginBottom: '15px'
                }}
                >
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.7',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}
                >
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* SECTION AVANTAGES DE NOTRE SOLUTION */}
      <motion.section
        style={{
          ...sectionStyle,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%)'
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <div style={shapesStyles}>
          <div style={{
            ...circleShape,
            width: '350px',
            height: '350px',
            top: '50%',
            left: '-150px',
            background: 'rgba(58, 134, 255, 0.08)',
          }}
          />
          <div style={{
            ...circleShape,
            width: '250px',
            height: '250px',
            bottom: '10%',
            right: '-100px',
            background: 'rgba(255, 0, 110, 0.08)',
          }}
          />
        </div>

        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              ...sectionHeadingStyle,
              position: 'relative',
              display: 'inline-block',
              marginBottom: '20px',
              textAlign: 'center',
              width: '100%'
            }}
          >
            Pourquoi choisir Drivesoft
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '50%',
              width: '60px',
              height: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              borderRadius: '2px',
              transform: 'translateX(-50%)'
            }}
            />
          </motion.h2>

          <motion.p
            variants={itemVariant}
            style={{
              ...sectionSubheadingStyle,
              marginBottom: '50px'
            }}
          >
            Découvrez comment notre solution peut transformer votre gestion du transport sanitaire
          </motion.p>

          <motion.div
            variants={staggerVariant}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '30px'
            }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariant}
                style={{
                  flex: '1 1 350px',
                  maxWidth: '380px',
                  background: 'white',
                  padding: '30px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.03)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  transition: 'transform 0.3s ease',
                }}
                whileHover={{
                  transform: 'translateY(-5px)',
                  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{
                  fontSize: '24px',
                  color: [
                    colors.primary, colors.secondary,
                    colors.accent, colors.success,
                    colors.warning, colors.primary
                  ][index % 6],
                  padding: '15px',
                  background: [
                    'rgba(58, 134, 255, 0.1)', 'rgba(255, 0, 110, 0.1)',
                    'rgba(131, 56, 236, 0.1)', 'rgba(6, 214, 160, 0.1)',
                    'rgba(255, 190, 11, 0.1)', 'rgba(58, 134, 255, 0.1)'
                  ][index % 6],
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
                >
                  {benefit.icon}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '10px',
                    color: colors.dark
                  }}
                  >
                    {benefit.title}
                  </h3>
                  <p style={{
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                    color: '#64748b',
                    margin: 0
                  }}
                  >
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default AboutUs;
